import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CUSTOM_IMAGE, FALLBACK_IMG, waLink, REVEAL } from "@/lib/site";

const STEPS = [
  { step: "01", title: "Share Your Idea", detail: "Send a photo, sketch or your room measurements on WhatsApp." },
  { step: "02", title: "Choose Materials", detail: "Pick your timber, finish and fabric with our guidance." },
  { step: "03", title: "We Build It", detail: "Crafted in our Mumbai workshop with progress photos shared with you." },
  { step: "04", title: "Delivered & Installed", detail: "Careful delivery and assembly at your home in Mumbai, Navi Mumbai or Thane." },
];

const CUSTOM_MESSAGE =
  "Hello JK Furniture, I want to customize furniture for my space. Here are my requirements and measurements:";

export const Custom = () => (
  <section id="custom" data-testid="custom-furniture-section" className="py-20 sm:py-28">
    <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <motion.p variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="eyebrow">
          06 / Bespoke Commissions
        </motion.p>
        <motion.h2
          variants={REVEAL} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-walnut max-w-xl"
        >
          Have A Design In Mind? <span className="italic text-teak">We'll Build It To The Millimetre.</span>
        </motion.h2>
        <motion.p
          variants={REVEAL} custom={2} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="mt-5 max-w-xl text-base leading-relaxed text-stone-600"
        >
          A wardrobe for an awkward corner, a TV unit sized to your wall, a bed with
          exactly the storage you need — tell us your space, measurements and requirements,
          and our carpenters will craft furniture that fits like it was always there.
        </motion.p>

        <div className="mt-10 space-y-0 max-w-xl">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              variants={REVEAL} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              className="flex gap-5 border-b border-[#E7DFD5] py-5 last:border-0"
            >
              <span className="font-mono text-sm text-brass pt-1">{s.step}</span>
              <div>
                <p className="font-semibold text-walnut">{s.title}</p>
                <p className="mt-1 text-sm text-stone-600">{s.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.a
          variants={REVEAL} custom={4} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          data-testid="btn-customize-furniture"
          href={waLink(CUSTOM_MESSAGE)}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-9 inline-flex items-center gap-3 rounded-full bg-brass px-8 py-4 text-sm font-semibold text-noir transition-all duration-300 hover:bg-walnut hover:text-sand"
        >
          Customize Your Furniture
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </motion.a>
      </div>

      <motion.div
        variants={REVEAL} custom={2} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
        className="lg:col-span-5 relative"
      >
        <div className="absolute -top-4 -right-4 h-full w-full border border-brass/50 rounded-2xl pointer-events-none" />
        <div className="img-frame rounded-2xl aspect-[4/5] max-h-[70vh] w-full">
          <img
            src={CUSTOM_IMAGE}
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
            alt="Carpenter crafting custom wooden furniture at JK Furniture workshop"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      </motion.div>
    </div>
  </section>
);
